import React from 'react';
import { Box, Flex, HStack, Image, Link, Text, VStack } from '@chakra-ui/react';
import { Link as RouterLink, Outlet } from 'react-router-dom';
import { useAuth } from '@kuow/shared-ui';

import kuowLockup from '../../assets/kuow-npr-lockup.svg';
import facebookIcon from '../../assets/icon-facebook.svg';
import instagramIcon from '../../assets/icon-instagram.svg';
import linkedinIcon from '../../assets/icon-linkedin.svg';
import nprLogo from '../../assets/npr-logo.png';
import tiktokIcon from '../../assets/icon-tiktok.svg';
import youtubeIcon from '../../assets/icon-youtube.svg';
import { getMainWebsiteUrl } from '@kuow/core';
import { ProfileMenu } from './ProfileMenu';
import { NeedHelpButton } from '../../support/NeedHelpButton';

export const PublicShell: React.FC = () => {
  const [hideHeaderFooter, setHideHeaderFooter] = React.useState(false);
  const appVersion = import.meta.env.VITE_APP_VERSION ?? 'dev';
  const { isAuthenticated, initialized } = useAuth();

  return (
    <Box minH="100vh" minW="80" bg="gray.solid" display="flex" flexDirection="column">
      {!hideHeaderFooter ? (
        <Box as="header" bg="bg" borderBottom="1px solid" borderColor="fg">
          <Flex
            minH="15"
            maxW="7xl"
            w="full"
            mx="auto"
            px={{ base: 6, md: 6 }}
            py="3"
            align="center"
          >
            <HStack gap={3}>
              <Link href={getMainWebsiteUrl()}>
                <Image src={String(kuowLockup)} alt="KUOW" h={{ base: 6, md: 9 }} />
              </Link>
            </HStack>

            <Box flex="1" />

            <NeedHelpButton />

            {initialized && isAuthenticated ? (
              <ProfileMenu />
            ) : (
              <Link
                asChild
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
                color="fg.muted"
              >
                <RouterLink to="/sign-in">Sign In</RouterLink>
              </Link>
            )}
          </Flex>
        </Box>
      ) : null}

      <Box as="main" flex="1" w="full" display="flex" flexDirection="column">
        <Outlet context={{ setHideHeaderFooter }} />
      </Box>

      {!hideHeaderFooter ? (
        <Box as="footer" bg="fg" color="fg.inverted">
          <Box maxW="7xl" w="full" mx="auto" px={{ base: 6, md: 6 }} py={{ base: 5, md: 0 }}>
            <Flex display={{ base: 'none', md: 'flex' }} py={4} align="center" gap={4}>
              <Image src={String(nprLogo)} alt="NPR" h="6" opacity={0.75} alignSelf="flex-start" mt={1} />

              <VStack flex="1" align="start" gap={1}>
                <Text fontSize="xs" color="fg.inverted">
                  <Link href="https://www.kuow.org/privacy-policy" color="fg.inverted">
                    Privacy Policy
                  </Link>{' '}
                  <Text as="span" opacity={0.35}>
                    |
                  </Text>{' '}
                  <Link href="https://www.kuow.org/terms" color="fg.inverted">
                    Terms of Use
                  </Link>
                </Text>
                <Text fontSize="xs" color="fg.inverted">
                  © {new Date().getFullYear()} KUOW News and Information{' '}
                  <Text as="span" opacity={0.35}>
                    —
                  </Text>{' '}
                  <Text as="span" fontSize="2xs" opacity={0.7}>
                    v{appVersion}
                  </Text>
                </Text>
                <Text fontSize="2xs" color="fg.inverted" opacity={0.7}>
                  KUOW is a 501(c)(3) tax-exempt nonprofit organization registered in the US under EIN 91–2079402
                </Text>
                <Text fontSize="2xs" color="fg.inverted" opacity={0.7}>
                  KUOW is a registered trademark of the University of Washington.
                </Text>
              </VStack>

              <HStack gap={2} alignSelf="flex-start" mt={1}>
                <Link href="https://www.facebook.com/kuow/" aria-label="KUOW on Facebook">
                  <Image src={String(facebookIcon)} alt="" boxSize="9" />
                </Link>
                <Link href="https://www.instagram.com/kuow/" aria-label="KUOW on Instagram">
                  <Image src={String(instagramIcon)} alt="" boxSize="9" />
                </Link>
                <Link href="https://www.linkedin.com/company/kuow/" aria-label="KUOW on LinkedIn">
                  <Image src={String(linkedinIcon)} alt="" boxSize="9" />
                </Link>
                <Link href="https://www.tiktok.com/@kuow" aria-label="KUOW on TikTok">
                  <Image src={String(tiktokIcon)} alt="" boxSize="9" />
                </Link>
                <Link href="https://www.youtube.com/@KUOW" aria-label="KUOW on YouTube">
                  <Image src={String(youtubeIcon)} alt="" boxSize="9" />
                </Link>
              </HStack>
            </Flex>

            <Box display={{ base: 'block', md: 'none' }}>
              <Flex align="center" justify="space-between" gap={4}>
                <Image src={String(nprLogo)} alt="NPR" h="6" opacity={0.75} />
                <HStack gap={2}>
                  <Link href="https://www.facebook.com/kuow/" aria-label="KUOW on Facebook">
                    <Image src={String(facebookIcon)} alt="" boxSize="9" />
                  </Link>
                  <Link href="https://www.instagram.com/kuow/" aria-label="KUOW on Instagram">
                    <Image src={String(instagramIcon)} alt="" boxSize="9" />
                  </Link>
                  <Link href="https://www.linkedin.com/company/kuow/" aria-label="KUOW on LinkedIn">
                    <Image src={String(linkedinIcon)} alt="" boxSize="9" />
                  </Link>
                  <Link href="https://www.tiktok.com/@kuow" aria-label="KUOW on TikTok">
                    <Image src={String(tiktokIcon)} alt="" boxSize="9" />
                  </Link>
                  <Link href="https://www.youtube.com/@KUOW" aria-label="KUOW on YouTube">
                    <Image src={String(youtubeIcon)} alt="" boxSize="9" />
                  </Link>
                </HStack>
              </Flex>

              <VStack align="start" gap={2} mt={4}>
                <Text fontSize="xs" color="fg.inverted">
                  <Link href="https://www.kuow.org/privacy-policy" color="fg.inverted">
                    Privacy Policy
                  </Link>{' '}
                  <Text as="span" opacity={0.35}>
                    |
                  </Text>{' '}
                  <Link href="https://www.kuow.org/terms" color="fg.inverted">
                    Terms of Use
                  </Link>
                </Text>
                <Text fontSize="xs" color="fg.inverted">
                  © {new Date().getFullYear()} KUOW News and Information{' '}
                  <Text as="span" opacity={0.35}>
                    —
                  </Text>{' '}
                  <Text as="span" fontSize="2xs" opacity={0.7}>
                    v{appVersion}
                  </Text>
                </Text>
                <Text fontSize="2xs" color="fg.inverted" opacity={0.7}>
                  KUOW is a 501(c)(3) tax-exempt nonprofit organization registered in the US under EIN 91–2079402
                </Text>
                <Text fontSize="2xs" color="fg.inverted" opacity={0.7}>
                  KUOW is a registered trademark of the University of Washington.
                </Text>
              </VStack>
            </Box>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};


